import React, { useState, useEffect } from 'react';
import CalculadoraHC from './CalculadoraHidratos/CalculadoraHC';
import Diario from './CalculadoraHidratos/Diario';
import CalculadoraUGP from './CalculadoraHidratos/CalculadoraUGP';
import Alimentos from './CalculadoraHidratos/Alimentos';
import { stringToNumber, formatearFecha, isoToLocalDatetime, localDatetimeToIso, formatearNumero, getLocalFoods, saveLocalFood, clearLocalFoods, getLocalDiario, saveLocalDiarioEntry, clearLocalDiario, getDeletedCustomFoods, addDeletedCustomFood, getHiddenCommonFoods, addHiddenCommonFood, removeHiddenCommonFood } from './utils';
import { saveFood, saveDiarioEntry, fetchFoods, fetchDiarioEntries, fetchCommonFoods, saveCommonFoodsToLocal, getCommonFoodsFromLocal, savePendingCommonFood, getPendingCommonFoods, clearPendingCommonFoods } from "./utils";
import { loginWithGoogle, logout, auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { deleteDoc, doc, setDoc } from "firebase/firestore";
import { db } from "../firebase";

// Merge local and Firebase foods, avoiding duplicates by name (case-insensitive)
function mergeFoods(localFoods, firebaseFoods) {
  const deleted = getDeletedCustomFoods();
  const map = new Map();
  firebaseFoods.forEach(f => {
    if (!deleted.includes(f.nombre.toLowerCase())) map.set(f.nombre.toLowerCase(), f);
  });
  localFoods.forEach(f => {
    if (!deleted.includes(f.nombre.toLowerCase()) && !map.has(f.nombre.toLowerCase())) map.set(f.nombre.toLowerCase(), f);
  });
  return Array.from(map.values());
}

// Merge local and Firebase diario entries, avoiding duplicates by fecha+alimentos
function mergeDiario(localDiario, firebaseDiario) {
  const key = e => `${e.fecha}|${(e.alimentos||[]).map(a=>a.nombre).join(',')}`;
  const map = new Map();
  firebaseDiario.forEach(e => map.set(key(e), e));
  localDiario.forEach(e => {
    if (!map.has(key(e))) map.set(key(e), e);
  });
  return Array.from(map.values());
}

const CalculadoraHidratos = () => {
  // Estado para controlar la visibilidad del menú
  const [menuAbierto, setMenuAbierto] = useState(false);
  
  // Estado para alimentos y cantidades
  const [alimentos, setAlimentos] = useState([
    { id: 1, nombre: '', cantidad: 0, unidad: 'g', carbsPor100: 0 }
  ]);

  // Estados para glucosa e insulina
  const [glucosaActual, setGlucosaActual] = useState('');
  const [unidadesInsulina, setUnidadesInsulina] = useState('');

  // Estado para el total
  const [totalCarbs, setTotalCarbs] = useState(0);

  // Estado para alimentos personalizados guardados
  const [alimentosPersonalizados, setAlimentosPersonalizados] = useState([]);

  // Estado para alimentos comunes (pre-made foods from Firebase)
  const [alimentosComunes, setAlimentosComunes] = useState([]);

  // Estado para controlar la vista actual (calculadora, diario, alimentos, calculadoraUGP)
  const [vistaActual, setVistaActual] = useState(() => {
    return localStorage.getItem('vistaActual') || 'calculadora';
  });

  // Estado para las entradas del diario
  const [entradasDiario, setEntradasDiario] = useState([]);
  
  // Estado para el modal de edición del diario
  const [modalEdicionAbierto, setModalEdicionAbierto] = useState(false);
  const [entradaEditando, setEntradaEditando] = useState(null);
  
  // Estado para los valores de la calculadora UGP
  const [valoresUGP, setValoresUGP] = useState({
    pesoTotal: '',
    grasasPor100: '',
    hidratosPor100: '',
    proteinasPor100: ''
  });
  
  // Estado para mostrar el tick
  const [showTick, setShowTick] = useState(false);

  // Estado para el usuario autenticado
  const [usuario, setUsuario] = useState(null);

  // Estado para saber si hay conexión
  const [online, setOnline] = useState(navigator.onLine);

  // Estado para alimentos comunes ocultos
  const [hiddenCommonFoods, setHiddenCommonFoods] = useState(getHiddenCommonFoods());

  // Nuevo estado para forzar actualización de alimentos
  const [refreshAlimentos, setRefreshAlimentos] = useState(0);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUsuario(user);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Sincronizar alimentos locales y Firebase al iniciar sesión
  useEffect(() => {
    if (usuario) {
      (async () => {
        // 1. Obtener alimentos locales y de Firebase
        let firebaseFoods = await fetchFoods();
        const localFoodsRaw = localStorage.getItem('customFoods');
        let localFoods = localFoodsRaw ? JSON.parse(localFoodsRaw) : [];
        const deleted = getDeletedCustomFoods();
        // 2. Subir a Firebase los alimentos locales que no estén en Firebase ni en eliminados
        for (const food of localFoods) {
          if (!firebaseFoods.some(f => f.nombre.toLowerCase() === food.nombre.toLowerCase() && f.carbsPor100 === food.carbsPor100) && !deleted.includes(food.nombre.toLowerCase())) {
            await saveFood(food);
          }
        }
        // 3. Volver a obtener la lista de Firebase tras subir
        firebaseFoods = await fetchFoods();
        // 4. Limpiar localStorage: eliminar los alimentos que ya están en Firebase
        const cleanedLocal = localFoods.filter(f => !firebaseFoods.some(ff => ff.nombre.toLowerCase() === f.nombre.toLowerCase() && ff.carbsPor100 === f.carbsPor100));
        localStorage.setItem('customFoods', JSON.stringify(cleanedLocal));
        // 5. Marcar como eliminados los que estén en local pero no en Firebase (por si se borraron en otro dispositivo)
        for (const food of localFoods) {
          if (!firebaseFoods.some(f => f.nombre.toLowerCase() === food.nombre.toLowerCase() && f.carbsPor100 === f.carbsPor100) && !deleted.includes(food.nombre.toLowerCase())) {
            addDeletedCustomFood(food.nombre);
          }
        }
        // 6. Filtrar los eliminados
        const deletedNow = getDeletedCustomFoods();
        firebaseFoods = firebaseFoods.filter(f => !deletedNow.includes(f.nombre.toLowerCase()));
        setAlimentosPersonalizados(firebaseFoods);
      })();
    } else {
      setAlimentosPersonalizados(getLocalFoods());
    }
  }, [usuario, alimentosComunes]);

  // Inicializar alimentos personalizados desde localStorage al montar o si cambia usuario
  useEffect(() => {
    if (!usuario) {
      setAlimentosPersonalizados(getLocalFoods());
    }
  }, [usuario]);

  // Sincronizar diario local y Firebase al iniciar sesión
  useEffect(() => {
    if (usuario) {
      (async () => {
        const localDiario = getLocalDiario();
        let firebaseDiario = await fetchDiarioEntries();
        // Upload local entries not in Firebase
        for (const entry of localDiario) {
          if (!firebaseDiario.some(e => e.fecha === entry.fecha && JSON.stringify(e.alimentos) === JSON.stringify(entry.alimentos))) {
            await saveDiarioEntry(entry);
          }
        }
        firebaseDiario = await fetchDiarioEntries();
        setEntradasDiario(mergeDiario(localDiario, firebaseDiario).sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
      })();
    } else {
      setEntradasDiario(getLocalDiario());
    }
  }, [usuario]);

  // Fetch pre-made foods from Firebase on app load, fallback a localStorage si falla
  useEffect(() => {
    const loadCommonFoods = async () => {
      try {
        const commonFoods = await fetchCommonFoods();
        if (commonFoods && commonFoods.length > 0) {
          setAlimentosComunes(commonFoods);
          saveCommonFoodsToLocal(commonFoods);
        } else {
          const localFoods = getCommonFoodsFromLocal();
          setAlimentosComunes(localFoods);
        }
      } catch (e) {
        const localFoods = getCommonFoodsFromLocal();
        setAlimentosComunes(localFoods);
      }
    };
    loadCommonFoods();
  }, []);

  // Sincroniza alimentos comunes pendientes cuando vuelve la conexión
  useEffect(() => {
    if (online) {
      const syncPendingCommonFoods = async () => {
        const pending = getPendingCommonFoods();
        if (pending.length > 0) {
          for (const food of pending) {
            try {
              // El id será el nombre normalizado
              const id = food.nombre.toLowerCase().replace(/\s+/g, '_');
              await setDoc(doc(db, 'commonFoods', id), food);
            } catch (e) {
              // Si falla, lo dejamos en pending
              console.error('Error subiendo alimento común pendiente:', food, e);
            }
          }
          clearPendingCommonFoods();
          // Recarga la lista desde Firebase
          const commonFoods = await fetchCommonFoods();
          setAlimentosComunes(commonFoods);
          saveCommonFoodsToLocal(commonFoods);
        }
      };
      syncPendingCommonFoods();
    }
  }, [online]);

  // Sincronizar hiddenCommonFoods con localStorage
  useEffect(() => {
    setHiddenCommonFoods(getHiddenCommonFoods());
  }, [usuario]);

  useEffect(() => {
    localStorage.setItem('vistaActual', vistaActual);
  }, [vistaActual]);
  
  // Calcular el total de carbohidratos
  useEffect(() => {
    const total = alimentos.reduce((sum, alimento) => {
      const carbs = (alimento.cantidad * alimento.carbsPor100) / 100;
      return sum + carbs;
    }, 0);
    
    setTotalCarbs(total);
  }, [alimentos]);
  
  // Manejar cambios en los campos de alimentos
  const handleAlimentoChange = (id, campo, valor) => {
    setAlimentos(prev => 
      prev.map(alimento => 
        alimento.id === id ? { ...alimento, [campo]: valor } : alimento
      )
    );
  };

  // Función para manejar cambios en campos numéricos (permite coma y punto como separador decimal)
  const handleNumeroChange = (id, campo, valor) => {
    // Permitir entrada de dígitos, un solo punto o coma
    if (valor === '' || /^-?\d*[.,]?\d{0,2}$/.test(valor)) {
      // Para almacenar en el estado, convertimos a número solo al perder el foco
      handleAlimentoChange(id, campo, valor);
    }
  };
  
  // Autocompletar alimento y su valor de carbohidratos
  const autocompletarAlimento = (id, nombre) => {
    // Buscar primero en alimentos personalizados
    let alimentoEncontrado = alimentosPersonalizados.find(a => a.nombre === nombre);
    
    // Si no se encuentra, buscar en alimentos comunes
    if (!alimentoEncontrado) {
      alimentoEncontrado = alimentosComunes.find(a => a.nombre === nombre);
    }
    
    if (alimentoEncontrado) {
      handleAlimentoChange(id, 'carbsPor100', alimentoEncontrado.carbsPor100);
    }
  };
  
  // Añadir nuevo alimento a la lista (usado por CalculadoraHC y Alimentos)
  const agregarAlimento = (nuevoAlimento = null) => {
    if (nuevoAlimento && nuevoAlimento.nombre) {
      const nuevoId = alimentos.length > 0 ? Math.max(...alimentos.map(a => a.id)) + 1 : 1;
      setAlimentos([...alimentos, { ...nuevoAlimento, id: nuevoId, cantidad: 0, unidad: 'g' }]);
    } else {
      const nuevoId = alimentos.length > 0 ? Math.max(...alimentos.map(a => a.id)) + 1 : 1;
      setAlimentos([...alimentos, { id: nuevoId, nombre: '', cantidad: 0, unidad: 'g', carbsPor100: 0 }]);
    }
  };

  // Para añadir desde Alimentos, función que puede ser pasada como prop
  const agregarAlimentoDesdeAlimentos = (alimento) => {
    if (alimento && alimento.nombre) {
      agregarAlimento(alimento);
      setVistaActual('calculadora'); // Opcional: volver a la calculadora tras añadir
    }
  };

  // Al guardar un alimento personalizado, añadirlo a la lista y a localStorage o Firebase
  const handleAddCustomFood = async (food) => {
    const deleted = getDeletedCustomFoods();
    if (deleted.includes(food.nombre.toLowerCase())) {
      // Si está en eliminados, lo quitamos de la lista de eliminados
      const nuevos = deleted.filter(n => n !== food.nombre.toLowerCase());
      localStorage.setItem('deletedCustomFoods', JSON.stringify(nuevos));
    }
    if (usuario) {
      const saveResult = await saveFood(food);
      if (saveResult) {
        const updatedFoods = await fetchFoods();
        if (Array.isArray(updatedFoods) && updatedFoods.length > 0) {
          setAlimentosPersonalizados(updatedFoods);
        } else {
          // If fetch fails or returns empty, do not wipe out state
          console.error("Error: fetchFoods returned empty or failed after save.");
        }
      } else {
        // Save failed, do not update state
        console.error("Error: saveFood failed.");
      }
    } else {
      let localFoods = getLocalFoods().filter(a => a.nombre.toLowerCase() !== food.nombre.toLowerCase());
      localFoods.push(food);
      localStorage.setItem('customFoods', JSON.stringify(localFoods));
      setAlimentosPersonalizados(localFoods);
    }
  };

  // Función para añadir alimento común (solo admin o modo especial, aquí ejemplo para offline)
  const handleAddCommonFood = async (food) => {
    if (online) {
      try {
        const id = food.nombre.toLowerCase().replace(/\s+/g, '_');
        await setDoc(doc(db, 'commonFoods', id), food);
        // Actualiza la lista local y backup
        const commonFoods = await fetchCommonFoods();
        setAlimentosComunes(commonFoods);
        saveCommonFoodsToLocal(commonFoods);
      } catch (e) {
        // Si falla, guarda como pendiente
        savePendingCommonFood(food);
        setAlimentosComunes(prev => [...prev, food]);
      }
    } else {
      // Sin conexión: guarda como pendiente y en la lista local
      savePendingCommonFood(food);
      setAlimentosComunes(prev => [...prev, food]);
    }
  };

  const handleHideCommonFood = (nombre) => {
    addHiddenCommonFood(nombre);
    setHiddenCommonFoods(getHiddenCommonFoods());
  };

  const handleShowCommonFood = (nombre) => {
    removeHiddenCommonFood(nombre);
    setHiddenCommonFoods(getHiddenCommonFoods());
  };

  // Eliminar un alimento de la lista
  const eliminarAlimento = (id) => {
    if (alimentos.length > 1) {
      setAlimentos(alimentos.filter(alimento => alimento.id !== id));
    }
  };
  
  // Limpiar la calculadora
  const limpiarCalculadora = () => {
    setAlimentos([{ id: 1, nombre: '', cantidad: 0, unidad: 'g', carbsPor100: 0 }]);
  };

  // Guardar entrada en diario (local o Firebase)
  const handleAddDiarioEntry = async (entry) => {
    if (usuario) {
      const saveResult = await saveDiarioEntry(entry);
      if (saveResult) {
        const latest = await fetchDiarioEntries();
        if (Array.isArray(latest) && latest.length > 0) {
          setEntradasDiario(latest.sort((a, b) => new Date(b.fecha) - new Date(a.fecha)));
        } else {
          // If fetch fails or returns empty, do not wipe out state
          console.error("Error: fetchDiarioEntries returned empty or failed after save.");
        }
      } else {
        // Save failed, do not update state
        console.error("Error: saveDiarioEntry failed.");
      }
    } else {
      setEntradasDiario(prev => [entry, ...prev]);
      saveLocalDiarioEntry(entry);
    }
  };
  
  // Añadir entrada al diario
  const añadirAlDiario = async () => {
    // Verificar que al menos un alimento tenga nombre
    const hayAlimentos = alimentos.some(a => a.nombre.trim() !== '');
    
    if (!hayAlimentos) {
      alert('Debes añadir al menos un alimento antes de guardar en el diario');
      return;
    }
    
    // Crear entrada de diario
    const fechaActual = new Date();
    const nuevaEntrada = {
      fecha: fechaActual.toISOString(),
      alimentos: [...alimentos],
      totalCarbs: totalCarbs,
      glucosa: glucosaActual !== '' ? parseInt(glucosaActual) : null,
      insulina: unidadesInsulina !== '' ? parseInt(unidadesInsulina) : null
    };
    
    // Añadir al estado y guardar (local o Firebase)
    await handleAddDiarioEntry(nuevaEntrada);
    
    // Limpiar los campos de glucosa e insulina
    setGlucosaActual('');
    setUnidadesInsulina('');
    
    // Mostrar animación de tick
    setShowTick(true);
    setTimeout(() => setShowTick(false), 1000);
  };
  
  // Editar entrada del diario
  const editarEntradaDiario = (entrada) => {
    // Crear una copia profunda para evitar modificar el estado original directamente
    const copiaEntrada = JSON.parse(JSON.stringify(entrada));
    setEntradaEditando(copiaEntrada);
    setModalEdicionAbierto(true);
  };
  
  // Guardar cambios en la entrada del diario
  const guardarCambiosEntrada = () => {
    if (!entradaEditando) return;
    
    // Actualizar la entrada en el array
    const nuevasEntradas = entradasDiario.map(entrada => 
      entrada.id === entradaEditando.id ? entradaEditando : entrada
    );
    
    setEntradasDiario(nuevasEntradas);
    
    // Guardar en Firestore
    try {
      saveDiarioEntry(entradaEditando);
      console.log("Diario entry updated online.");
    } catch (error) {
      console.error("Error updating diario entry online:", error);
    }
    
    // Cerrar modal
    setModalEdicionAbierto(false);
    setEntradaEditando(null);
  };
  
  // Eliminar entrada del diario
  const eliminarEntradaDiario = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar esta entrada del diario?')) {
      // Remove from state
      setEntradasDiario(prev => prev.filter(entrada => entrada.id !== id));
      // Remove from Firestore if logged in
      if (usuario) {
        try {
          await deleteDoc(doc(db, "diario", id));
        } catch (error) {
          console.error("Error deleting diario entry from Firestore:", error);
        }
      } else {
        // Remove from localStorage
        const localDiario = getLocalDiario().filter(entrada => entrada.id !== id);
        localStorage.setItem('customDiario', JSON.stringify(localDiario));
      }
    }
  };
  
  // Actualizar total de carbohidratos de la entrada editando
  useEffect(() => {
    if (entradaEditando) {
      const total = entradaEditando.alimentos.reduce((sum, alimento) => {
        const carbs = (alimento.cantidad * alimento.carbsPor100) / 100;
        return sum + carbs;
      }, 0);
      
      setEntradaEditando({
        ...entradaEditando,
        totalCarbs: total
      });
    }
  }, [entradaEditando?.alimentos]);
  
  // Manejar cambios en los alimentos de la entrada editando
  const handleEntradaAlimentoChange = (id, campo, valor) => {
    if (!entradaEditando) return;
    setEntradaEditando({
      ...entradaEditando,
      alimentos: entradaEditando.alimentos.map(alimento =>
        alimento.id === id ? { ...alimento, [campo]: valor } : alimento
      )
    });
  };

  // Manejar cambios numéricos en la entrada editando
  const handleEntradaNumeroChange = (id, campo, valor) => {
    // Permitir entrada de dígitos, un solo punto o coma
    if (valor === '' || /^-?\d*[.,]?\d{0,2}$/.test(valor)) {
      handleEntradaAlimentoChange(id, campo, valor);
    }
  };

  // Cargar datos guardados
  useEffect(() => {
    const loadUserData = async () => {
      try {
        const userFoods = await fetchFoods();
        setAlimentosPersonalizados(userFoods);
      } catch (error) {
        console.error("Error fetching user foods:", error);
      }
    };
    loadUserData();
  }, []);

  // Calcula la lista de alimentos visibles según login y ocultos
  const alimentosVisibles = usuario
    ? [
        // Solo mostrar alimentos comunes que NO estén personalizados por el usuario y no estén ocultos
        ...alimentosComunes.filter(
          ac => !alimentosPersonalizados.some(ap => ap.nombre.toLowerCase() === ac.nombre.toLowerCase()) && !hiddenCommonFoods.includes(ac.nombre.toLowerCase())
        ),
        ...alimentosPersonalizados
      ]
    : alimentosComunes.filter(ac => !hiddenCommonFoods.includes(ac.nombre.toLowerCase()));
  
  // Fetch latest alimentosPersonalizados from Firebase/local
  const fetchAndSetAlimentosPersonalizados = async () => {
    if (usuario) {
      let firebaseFoods = await fetchFoods();
      const localFoodsRaw = localStorage.getItem('customFoods');
      let localFoods = localFoodsRaw ? JSON.parse(localFoodsRaw) : [];
      const deleted = getDeletedCustomFoods();
      for (const food of localFoods) {
        if (!firebaseFoods.some(f => f.nombre.toLowerCase() === food.nombre.toLowerCase() && f.carbsPor100 === food.carbsPor100) && !deleted.includes(food.nombre.toLowerCase())) {
          await saveFood(food);
        }
      }
      firebaseFoods = await fetchFoods();
      const cleanedLocal = localFoods.filter(f => !firebaseFoods.some(ff => ff.nombre.toLowerCase() === f.nombre.toLowerCase() && ff.carbsPor100 === f.carbsPor100));
      localStorage.setItem('customFoods', JSON.stringify(cleanedLocal));
      for (const food of localFoods) {
        if (!firebaseFoods.some(f => f.nombre.toLowerCase() === food.nombre.toLowerCase() && f.carbsPor100 === f.carbsPor100) && !deleted.includes(food.nombre.toLowerCase())) {
          addDeletedCustomFood(food.nombre);
        }
      }
      const deletedNow = getDeletedCustomFoods();
      firebaseFoods = firebaseFoods.filter(f => !deletedNow.includes(f.nombre.toLowerCase()));
      setAlimentosPersonalizados(firebaseFoods);
    } else {
      setAlimentosPersonalizados(getLocalFoods());
    }
  };

  return (
    <div className="w-full mx-auto p-6 bg-white rounded-xl shadow-lg relative">
      {/* Tick animación */}
      {showTick && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="bg-white bg-opacity-80 rounded-full p-8 shadow-lg animate-fade-tick">
            <svg className="w-24 h-24 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 13l3 3 6-6" />
            </svg>
          </div>
        </div>
      )}
      {/* Contenedor principal con ancho adaptable */}
      <div className="max-w-6xl mx-auto">
        {/* Botón de menú hamburguesa */}
        <div className="absolute top-6 right-6 z-50">
          <button 
            onClick={() => setMenuAbierto(!menuAbierto)} 
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
            aria-label="Menú principal"
          >
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor" 
              className="w-6 h-6 text-indigo-600"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M4 6h16M4 12h16M4 18h16" 
              />
            </svg>
          </button>
        </div>
        
        {/* Menú desplegable */}
        <div 
          className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity duration-300 ${
            menuAbierto ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          onClick={() => setMenuAbierto(false)}
        >
          <div 
            className={`absolute w-full transform transition-transform duration-300 ease-in-out ${
              menuAbierto ? 'translate-y-0' : '-translate-y-full'
            } bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 max-w-6xl mx-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold">Menú</h2>
                <button 
                  onClick={() => setMenuAbierto(false)}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    stroke="currentColor" 
                    className="w-6 h-6"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      strokeWidth={2} 
                      d="M6 18L18 6M6 6l12 12" 
                    />
                  </svg>
                </button>
              </div>
              
              {/* Opciones de menú - responsive */}
              <nav className="flex flex-col sm:flex-row sm:justify-around">
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setVistaActual('home');
                    setMenuAbierto(false);
                  }}
                  className={`py-3 px-4 rounded-lg transition-colors mb-2 sm:mb-0 text-center font-medium ${
                    vistaActual === 'home' ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Home
                </a>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setVistaActual('calculadora');
                    setMenuAbierto(false);
                  }}
                  className={`py-3 px-4 rounded-lg transition-colors mb-2 sm:mb-0 text-center font-medium ${
                    vistaActual === 'calculadora' ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Calculadora HC
                </a>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setVistaActual('diario');
                    setMenuAbierto(false);
                  }}
                  className={`py-3 px-4 rounded-lg transition-colors mb-2 sm:mb-0 text-center font-medium ${
                    vistaActual === 'diario' ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Diario
                </a>
                <a 
                  href="#" 
                  onClick={(e) => {
                    e.preventDefault();
                    setVistaActual('calculadoraUGP');
                    setMenuAbierto(false);
                  }}
                  className={`py-3 px-4 rounded-lg transition-colors mb-2 sm:mb-0 text-center font-medium ${
                    vistaActual === 'calculadoraUGP' ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Calculadora UGP
                </a>
                <a 
                  href="#" 
                  onClick={async (e) => {
                    e.preventDefault();
                    setVistaActual('alimentos');
                    setMenuAbierto(false);
                    await fetchAndSetAlimentosPersonalizados(); // always fetch latest
                  }}
                  className={`py-3 px-4 rounded-lg transition-colors mb-2 sm:mb-0 text-center font-medium ${
                    vistaActual === 'alimentos' ? 'bg-white bg-opacity-30' : 'hover:bg-white hover:bg-opacity-20'
                  }`}
                >
                  Alimentos
                </a>
                <div className="flex justify-center mt-2 sm:mt-0">
                  {!usuario ? (
                    <button
                      onClick={loginWithGoogle}
                      className="flex items-center gap-2 bg-white text-gray-700 border border-gray-200 rounded-lg px-4 py-2 shadow-sm hover:bg-gray-100 transition-colors font-medium"
                      style={{ minWidth: 180 }}
                    >
                      <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                      Iniciar sesión con Google
                    </button>
                  ) : (
                    <button
                      onClick={logout}
                      className="flex items-center gap-2 bg-red-100 text-red-700 border border-red-200 rounded-lg px-4 py-2 shadow-sm hover:bg-red-200 transition-colors font-medium"
                      style={{ minWidth: 180 }}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-5 h-5">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7" />
                      </svg>
                      Cerrar Sesión
                    </button>
                  )}
                </div>
              </nav>
            </div>
          </div>
        </div>
        
        {/* Home view: descripción e intro */}
        {vistaActual === 'home' && (
          <div className="max-w-2xl mx-auto p-8 bg-white rounded-xl shadow-lg mt-8">
            <h1 className="text-3xl font-bold text-center text-indigo-600 mb-4">¿Qué puedes hacer en esta web?</h1>
            <p className="text-gray-700 text-base mb-4">
              Esta aplicación te ayuda a calcular los hidratos de carbono de tus comidas, llevar un diario de tus registros y gestionar tus propios alimentos personalizados. Puedes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4">
              <li><b>Calculadora HC:</b> Añade alimentos, indica la cantidad y obtén el total de hidratos de carbono de tu comida.</li>
              <li><b>Alimentos:</b> Consulta una lista de alimentos comunes y añade tus propios alimentos personalizados para usarlos fácilmente en la calculadora.</li>
              <li><b>Diario:</b> Guarda tus comidas, hidratos, glucosa e insulina para llevar un historial y ver tu evolución.</li>
              <li><b>Calculadora UGP:</b> Calcula las Unidades de Grasas y Proteínas de tus platos.</li>
            </ul>
            <p className="text-gray-700 text-base mb-4">
              Si inicias sesión, tus alimentos personalizados y tus registros del diario se guardarán en la nube. Así podrás acceder a ellos desde cualquier dispositivo, sin perder tus datos aunque cambies de móvil o uses varios dispositivos.
            </p>
            <div className="my-8">
              <div className="aspect-w-16 aspect-h-9 w-full bg-gray-200 rounded-lg flex items-center justify-center">
                {/* Aquí podrás incrustar tu video de YouTube */}
                <span className="text-gray-400">Próximamente: video explicativo</span>
                {/* Ejemplo de cómo incrustar el video más adelante:
                <iframe width=\"100%\" height=\"100%\" src=\"https://www.youtube.com/embed/ID_DEL_VIDEO\" title=\"YouTube video player\" frameBorder=\"0\" allow=\"accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share\" allowFullScreen></iframe>
                */}
              </div>
            </div>
          </div>
        )}
        
        {/* Vista de Calculadora */}
        {vistaActual === 'calculadora' && (
          <CalculadoraHC
            alimentos={alimentos}
            setAlimentos={setAlimentos}
            alimentosPersonalizados={alimentosPersonalizados}
            setAlimentosPersonalizados={setAlimentosPersonalizados}
            alimentosComunes={alimentosVisibles}
            glucosaActual={glucosaActual}
            setGlucosaActual={setGlucosaActual}
            unidadesInsulina={unidadesInsulina}
            setUnidadesInsulina={setUnidadesInsulina}
            totalCarbs={totalCarbs}
            handleAlimentoChange={handleAlimentoChange}
            handleNumeroChange={handleNumeroChange}
            autocompletarAlimento={autocompletarAlimento}
            agregarAlimento={agregarAlimento}
            eliminarAlimento={eliminarAlimento}
            limpiarCalculadora={limpiarCalculadora}
            añadirAlDiario={añadirAlDiario}
            formatearNumero={formatearNumero}
            saveFood={saveFood}
            fetchFoods={fetchFoods}
            onAddCustomFood={handleAddCustomFood}
          />
        )}
        
        {/* Vista de Alimentos */}
        {vistaActual === 'alimentos' && (
          <Alimentos
            alimentosComunes={alimentosComunes}
            alimentosPersonalizados={alimentosPersonalizados}
            setAlimentosPersonalizados={setAlimentosPersonalizados}
            agregarAlimento={agregarAlimentoDesdeAlimentos}
            onAddCustomFood={handleAddCustomFood}
            onAddCommonFood={handleAddCommonFood}
            hiddenCommonFoods={hiddenCommonFoods}
            onHideCommonFood={handleHideCommonFood}
            onShowCommonFood={handleShowCommonFood}
            vistaActual={vistaActual}
          />
        )}
        
        {/* Vista de Diario */}
        {vistaActual === 'diario' && (
          <Diario
            entradasDiario={entradasDiario}
            editarEntradaDiario={editarEntradaDiario}
            eliminarEntradaDiario={eliminarEntradaDiario}
            setVistaActual={setVistaActual}
            modalEdicionAbierto={modalEdicionAbierto}
            setModalEdicionAbierto={setModalEdicionAbierto}
            entradaEditando={entradaEditando}
            setEntradaEditando={setEntradaEditando}
            guardarCambiosEntrada={guardarCambiosEntrada}
            handleEntradaAlimentoChange={handleEntradaAlimentoChange}
            handleEntradaNumeroChange={handleEntradaNumeroChange}
            formatearFecha={formatearFecha}
            isoToLocalDatetime={isoToLocalDatetime}
            localDatetimeToIso={localDatetimeToIso}
            onAddDiaryEntry={handleAddDiarioEntry}
          />
        )}
        
        {/* Vista de Calculadora UGP */}
        {vistaActual === 'calculadoraUGP' && (
          <CalculadoraUGP
            valoresUGP={valoresUGP}
            setValoresUGP={setValoresUGP}
            setVistaActual={setVistaActual}
            stringToNumber={stringToNumber}
          />
        )}
      </div>
      <style>{`
        @keyframes fade-tick {
          0% { opacity: 0; transform: scale(0.8); }
          20% { opacity: 1; transform: scale(1.1); }
          80% { opacity: 1; transform: scale(1); }
          100% { opacity: 0; transform: scale(0.8); }
        }
        .animate-fade-tick {
          animation: fade-tick 1s cubic-bezier(0.4,0,0.2,1);
        }
      `}</style>
    </div>
  );
};

export default CalculadoraHidratos;